import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useSearch, useLocation } from 'wouter';
import Upcoming from '../components/Upcoming';
import { trackEvent } from '@/utils/analytics';
import { EventAction } from '@/types/analytics';
import usePartner from '@/hooks/usePartner';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { useAuthStore } from '@/hooks/useAuthStore';

const Quests = lazyWithRetry(() => import('./Quests'));
const CollectionDrop = lazyWithRetry(() => import('./CollectionDrop'));
const MyCollection = lazyWithRetry(() => import('./MyCollection'));
const PerksAndBenefit = lazyWithRetry(() => import('./PerksAndBenefit'));
const MyPoint = lazyWithRetry(() => import('./MyPoint'));

const TABS: string[] = [
    "Quests",
    "Collection Drop",
    "My Collection",
    "Perks & Benefit",
    "My Point",
    "separator",
    "Proposals",
    "Programs",
    "Treasury",
];

function TabPanel({ children, value, activeTab }: { children: React.ReactNode, value: string, activeTab: string }) {
  return (
    <>
      {value === activeTab && (
        <Suspense>
          {children}
        </Suspense>
      )}
    </>
  );
}

function Tabs() {
    const searchParams = useSearch();
    const [, setLocation] = useLocation();
    const [activeTab, setActiveTab] = useState<string>('');
    const { data: partner, error } = usePartner();
    const isAuthenticated = useAuthStore(state => state.isAuthenticated);

    // Filter tabs based on whether this is a DAO project
    const visibleTabs = useMemo(() => {
        let tabs = [...TABS];

        if (!partner?.isDaoProject) {
            // Show only non-DAO tabs
            tabs = tabs.filter(tab => !['separator', 'proposals', 'programs', 'treasury', 'perks & benefit'].includes(tab.toLowerCase()));
        }

        if (!isAuthenticated) {
            tabs = tabs.filter(tab => !['my collection', 'my point'].includes(tab.toLocaleLowerCase()));
        }

        // Show all tabs for DAO projects
        return tabs;
    }, [partner?.isDaoProject, isAuthenticated]);

    // Redirect to 404 if partner not found
    useEffect(function handlePartnerNotFound() {
        if (error?.code === 404) {
            setLocation("/404");
        }
    }, [error?.code])

    useEffect(function updateActiveTabBasedOnSearchParams() {
        const params = new URLSearchParams(searchParams);
        const tab = params.get('tab')?.toLowerCase() || 'quests';
        setActiveTab(tab);
    }, [searchParams]);

    function handleClickTab(tab: string) {
        const tabLower = tab.toLowerCase();
        const previousTab = activeTab;

        // Track tab click
        trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
            label: `xdao_tab_${tabLower.replace(/\s+/g, '_').replace(/&/g, '')}`,
            component: 'xdao_tabs',
            location: 'tab_navigation',
            tab_name: tabLower,
            previous_tab: previousTab
        });

        const url = new URL(window.location.href);
        url.search = '';
        url.searchParams.set('tab', tabLower);

        setActiveTab(tabLower);
        window.history.replaceState({}, '', url);
    }

    return (
        <>
            <div className='w-full overflow-x-hidden pl-8 lg:px-8'>
                <div className={`
                    flex flex-row gap-x-8 gap-y-3 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
                `}>
                    {visibleTabs.map((data: string) => {
                        const tab = data.toLocaleLowerCase();

                        if (tab === 'separator') {
                            return <span key="separator" className='font-inter text-base text-tertiary flex-shrink-0'>|</span>
                        }

                        return (
                            <div key={tab} className='flex-shrink-0'>
                                <button
                                    className={`font-inter text-base whitespace-nowrap ${tab === activeTab ? 'text-white': 'text-tertiary'}`}
                                    onClick={() => handleClickTab(tab)}
                                >
                                    {data}
                                </button>
                                <div className={`h-[2px] w-full mt-[0.66rem] bg-white ${tab === activeTab ? 'visible' : 'invisible'}`}/>
                            </div>
                        )
                    })}
                </div>
            </div>
            <div className='lg:px-8'>
                <div className='w-full h-[1px] bg-tertiary mb-6'/>
            </div>
            {visibleTabs.some((tab: string) => tab.toLocaleLowerCase() === activeTab) ? (
                <>
                    <TabPanel
                        value='quests'
                        activeTab={activeTab}
                    >
                        <div className='px-8'>
                            <Quests/>
                        </div>
                    </TabPanel>
                    <TabPanel
                        value='collection drop'
                        activeTab={activeTab}
                    >
                        <CollectionDrop/>
                    </TabPanel>
                    <TabPanel
                        value='my collection'
                        activeTab={activeTab}
                    >
                        <MyCollection/>
                    </TabPanel>
                    <TabPanel
                        value='perks & benefit'
                        activeTab={activeTab}
                    >
                        <div className='px-8'>
                            <PerksAndBenefit/>
                        </div>
                    </TabPanel>
                    <TabPanel
                        value='my point'
                        activeTab={activeTab}
                    >
                        <div className='px-8'>
                            <MyPoint/>
                        </div>
                    </TabPanel>
                    <TabPanel
                        value='proposals'
                        activeTab={activeTab}
                    >
                        <div className='px-8 flex col-span-full items-center justify-center mt-60'>
                            <Upcoming />
                        </div>
                    </TabPanel>
                    <TabPanel
                        value='programs'
                        activeTab={activeTab}
                    >
                        <div className='px-8 flex col-span-full items-center justify-center mt-60'>
                            <Upcoming />
                        </div>
                    </TabPanel>
                    <TabPanel
                        value='treasury'
                        activeTab={activeTab}
                    >
                        <div className='px-8 flex col-span-full items-center justify-center mt-60'>
                            <Upcoming />
                        </div>
                    </TabPanel>
                </>
            ) : (
                <span>Not Found</span>
            )}
        </>
    );
}

export default Tabs;